import React, {
  useEffect, useState, useCallback, useMemo, useRef,
} from "react";
import {
  Tooltip, Accordion, Button, Text, Flex,
} from "@wingmate/toolkit";
import { useTranslation } from "react-i18next";
import { ILeadSection } from "../../types/ICampaign";
import { FieldLabel } from "../../common/FieldLabel/FieldLabel";
import { FIELD_TYPES, InputField } from "../InputField/InputField";
import "./CreateForm.scss";
import { ICustomField, FieldValue } from "../../types/ICustomField";
import { IContactAttributes } from "../../types/IContact";
import { ContactSection } from "./ContactSection/ContactSection";
import { EMAIL_REGEX } from "../../utils/mentions";

interface IField {
  id: string;
  name?: string;
  defaultValue?: FieldValue;
  default?: string;
  disabled?: boolean;
  message?: string;
  messageType?: string;
  min?: number;
  multiple?: boolean;
  options?: Array<string | { label: string; value: string | number }>;
  placeholder?: string;
  required?: boolean;
  section?: string;
  type: (typeof FIELD_TYPES)[number];
  customField?: boolean;
}

export type IPayload = {
  [key: string]: FieldValue | Array<Partial<IContactAttributes>> | undefined;
} & {
  custom_fields: Record<string, FieldValue | undefined>;
  contacts?: Array<Partial<IContactAttributes>>;
};

export interface ICreateFormProps {
  afterSubmit?: () => void;
  cancelLabel?: string;
  customButtons?: React.ReactNode;
  fields: IField[];
  contactFields?: ICustomField[];
  isContactEnabled?: boolean;
  leadLabel?: string;
  leadSections?: ILeadSection[];
  onCancel?: () => void;
  onChange?: (payload: IPayload) => void;
  onSubmit: (payload: IPayload) => Promise<void>;
  requiredGroups?: string[][];
  submitLabel?: string;
  title?: string;
}

export const CreateForm: React.FC<ICreateFormProps> = ({
  afterSubmit,
  cancelLabel = "Cancel",
  contactFields = [],
  customButtons,
  fields,
  isContactEnabled = false,
  leadLabel,
  leadSections,
  onCancel,
  onChange,
  onSubmit,
  requiredGroups = [],
  submitLabel = "Submit",
  title,
}) => {
  const { t } = useTranslation(["default", "common"]);
  const [payload, setPayload] = useState<IPayload>({ custom_fields: {} } as IPayload);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const prevFieldsRef = useRef<IField[]>([]);

  const haveFieldsChanged = useCallback((prevFields: IField[], nextFields: IField[]) => {
    if (prevFields === nextFields) return false;
    if (!Array.isArray(prevFields) || !Array.isArray(nextFields)) return true;
    if (prevFields.length !== nextFields.length) return true;
    return prevFields.some((prev, index) => {
      const next = nextFields[index];
      if (!prev || !next) return true;

      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);

      return (
        prevKeys.length !== nextKeys.length
        || prevKeys.some((key) => prev[key as keyof IField] !== next[key as keyof IField])
      );
    });
  }, []);

  const defaultPayload = useCallback((fieldsList: IField[]): IPayload => {
    const result: IPayload = { custom_fields: {} } as IPayload;
    fieldsList.forEach((field) => {
      if (field.customField) {
        result.custom_fields = {
          ...result.custom_fields,
          [field.id]: field.defaultValue || field.default,
        };
      } else {
        result[field.id] = field.defaultValue || field.default;
      }
    });
    return result;
  }, []);

  const isFieldSet = useCallback(
    (field: IField) => {
      const value = field.customField
        ? payload.custom_fields?.[field.id]
        : payload?.[field.id];

      if (value === 0) return true;
      if (Array.isArray(value) && value.length === 0) return false;

      return !!value;
    },
    [payload],
  );

  const isGroupSet = useCallback(
    (group: string[]) => group.some((fieldId) => {
      const field = fields.find((f) => f.id === fieldId);
      return field ? isFieldSet(field) : false;
    }),
    [fields, isFieldSet],
  );

  const getMissingRequiredFields = useCallback(
    () => fields.filter((field) => field.required && !isFieldSet(field)),
    [fields, isFieldSet],
  );

  const getMissingContactFields = useCallback(() => (payload.contacts || []).reduce<Array<{
    index: number;
    missingName: boolean;
    invalidEmail: boolean;
  }>>((results, contact, index) => {
    const name = contact.name?.trim() || "";
    const email = contact.email?.trim() || "";
    const missingName = name === "";
    const invalidEmail = email !== "" && !EMAIL_REGEX.test(email);

    if (missingName || invalidEmail) {
      results.push({ index, missingName, invalidEmail });
    }

    return results;
  }, []), [payload.contacts]);

  const isSubmitDisabled = useMemo(() => {
    const allRequiredFieldsSet = getMissingRequiredFields().length === 0
      && getMissingContactFields().length === 0;
    const allGroupsSet = requiredGroups.every(isGroupSet);
    return isSubmitting || !(allRequiredFieldsSet && allGroupsSet);
  }, [isSubmitting, getMissingRequiredFields, requiredGroups, isGroupSet, getMissingContactFields]);

  const mergePayloadWithDefaults = useCallback((newFields: IField[]) => {
    const newPayload = defaultPayload(newFields);
    Object.keys(payload).forEach((key) => {
      if (key in newPayload && key !== "custom_fields") {
        newPayload[key] = payload[key];
      }
    });
    if (payload.custom_fields) {
      Object.keys(payload.custom_fields).forEach((key) => {
        if (key in (newPayload.custom_fields || {})) {
          newPayload.custom_fields[key] = payload.custom_fields[key];
        }
      });
    }
    return newPayload;
  }, [payload, defaultPayload]);

  useEffect(() => {
    if (!hasInitialized && fields.length > 0) {
      setPayload(defaultPayload(fields));
      setHasInitialized(true);
    } else if (
      hasInitialized
      && haveFieldsChanged(prevFieldsRef.current, fields)
    ) {
      setPayload(mergePayloadWithDefaults(fields));
    }
    prevFieldsRef.current = fields;
  }, [
    fields,
    defaultPayload,
    hasInitialized,
    haveFieldsChanged,
    mergePayloadWithDefaults,
  ]);

  const formatDependentRequiredFieldMessage = (fieldIds: string[]) => fieldIds
    .map((id, index) => {
      const field = fields.find((f) => f.id === id);
      if (!field) return "";
      return index === 0 ? `Must add ${field.name}` : `or ${field.name}`;
    })
    .join(" ");

  const formatDependentRequiredContactFieldMessage = (contact: {
    index: number;
    missingName: boolean;
    invalidEmail: boolean;
  }) => {
    const messages = [];
    if (contact.missingName) {
      messages.push(
        t("common:contact.errors.missingName", { index: contact.index + 1 }),
      );
    }
    if (contact.invalidEmail) {
      messages.push(
        t("common:contact.errors.invalidEmail", { index: contact.index + 1 }),
      );
    }
    return `Contact #${contact.index + 1} ${messages.join(" & ")}`;
  };

  const getErrors = () => {
    const groupErrors = requiredGroups
      .filter((group) => !isGroupSet(group))
      .map(formatDependentRequiredFieldMessage);

    const fieldErrors = getMissingRequiredFields().map(
      (f) => formatDependentRequiredFieldMessage([f.id]),
    );

    const contactErrors = getMissingContactFields().map(
      (contact) => formatDependentRequiredContactFieldMessage(contact),
    );

    return [...groupErrors, ...fieldErrors, ...contactErrors];
  };

  const handleChange = (
    data: {
      id: string;
      value: FieldValue | Array<Partial<IContactAttributes>>;
    },
    field: IField | { id: string; type: string },
  ) => {
    const newPayload = { ...payload };

    if (field.type === "contact") {
      newPayload.contacts = data.value as Array<Partial<IContactAttributes>>;
    } else if ("customField" in field && field.customField) {
      newPayload.custom_fields = {
        ...newPayload.custom_fields,
        [data.id]: data.value as FieldValue,
      };
    } else {
      newPayload[data.id] = data.value as FieldValue;
    }

    setPayload(newPayload as IPayload);
    onChange?.(newPayload as IPayload);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(payload);
      afterSubmit?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const createInputFieldKey = (field: IField) => (field.customField ? `${field.id}-custom` : field.id);

  const getFieldsForSection = (sectionId = "") => fields.filter(
    (field) => field.type !== "hidden"
        && (sectionId === ""
          ? !field.section || field.section === ""
          : field.section === sectionId),
  );

  const renderFieldsInSection = (sectionFields: IField[]) => sectionFields.map((field) => {
    const fieldKey = createInputFieldKey(field);
    return (
      <Flex vertical className="fields__field" key={fieldKey}>
        <FieldLabel
          label={field.name}
          svg={field.required}
          tooltipMessage={
            field.required ? t("common:fields.required") : undefined
          }
        />
        <InputField
          field={field}
          onChange={(data: { id: string; value: FieldValue }) => handleChange(data, field)}
        />
      </Flex>
    );
  });

  const renderFields = () => {
    const elements: JSX.Element[] = [];

    const leadInfoFields = getFieldsForSection();
    if (leadInfoFields.length > 0) {
      elements.push(
        <Flex
          vertical
          key="lead-info"
          className="CreateForm__section"
          align="stretch"
          gap="S"
        >
          <Accordion
            className="CreateForm__section__header"
            header={t("common:customFields.info", { label: leadLabel })}
            content={renderFieldsInSection(leadInfoFields)}
            defaultExpanded
          />
        </Flex>,
      );
    }

    leadSections?.forEach((section) => {
      if (section.type === "custom_fields") {
        const sectionFields = getFieldsForSection(section.id);
        if (sectionFields.length > 0) {
          elements.push(
            <Flex
              vertical
              key={section.id}
              className="CreateForm__section"
              align="stretch"
              gap="S"
            >
              <Accordion
                className="CreateForm__section__header"
                header={section.label}
                content={renderFieldsInSection(sectionFields)}
                defaultExpanded
              />
            </Flex>,
          );
        }
      }
    });

    return elements;
  };

  const submitButton = () => {
    const errors = getErrors();
    const disabled = isSubmitDisabled || errors.length > 0;
    const button = (
      <Button
        type="filled"
        onClick={handleSubmit}
        disabled={disabled}
        loading={isSubmitting}
      >
        {submitLabel}
      </Button>
    );
    return errors.length > 0 ? (
      <Tooltip title={errors.join(". ")}>
        <div>{button}</div>
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <Flex
      vertical
      className="CreateForm"
      align="center"
      justify="space-between"
    >
      {title && (
        <Text type="H5" className="CreateForm__title">
          {title}
        </Text>
      )}
      <Flex vertical className="CreateForm__fields" align="center" gap="S">
        {renderFields()}
        {isContactEnabled && (
          <ContactSection
            fieldId="contacts"
            onChange={(data) => handleChange(data, { id: data.id, type: "contact" })}
            contactFields={contactFields}
            payload={payload}
          />
        )}
      </Flex>
      <Flex
        className="CreateForm__buttons"
        align="center"
        justify="space-between"
        gap="S"
      >
        <div>{customButtons}</div>
        <Flex gap="S">
          {!isSubmitting && onCancel && (
            <Button type="text" onClick={onCancel}>
              {cancelLabel}
            </Button>
          )}
          {submitButton()}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CreateForm;
